import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setAuthToken, loadStoredToken, extractErr } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async (opts = {}) => {
    const { retries = 2, retryDelayMs = 600 } = opts;
    let lastErr = null;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user);
        setCompany(data.company);
        return data;
      } catch (e) {
        lastErr = e;
        const status = e?.response?.status;
        // Real auth failures: stop retrying and clear session immediately.
        if (status === 401 || status === 403) {
          setAuthToken(null);
          setUser(null);
          setCompany(null);
          return null;
        }
        // Transient errors (network blip, 5xx, timeout): wait then retry.
        if (attempt < retries) {
          await new Promise((r) => setTimeout(r, retryDelayMs * (attempt + 1)));
        }
      }
    }
    // After all retries exhausted on a transient error, do NOT nuke the user
    // if we still have a token — let the next API call retry. Only nuke if
    // there's no token at all (initial load with bad token).
    if (!localStorage.getItem("operon_token")) {
      setUser(null);
      setCompany(null);
    }
    return null;
  }, []);

  // Listen for global 401 events from the axios interceptor and clear state.
  useEffect(() => {
    const onLogout = () => {
      setUser(null);
      setCompany(null);
    };
    window.addEventListener("operon:auth:logout", onLogout);
    return () => window.removeEventListener("operon:auth:logout", onLogout);
  }, []);

  useEffect(() => {
    const token = loadStoredToken();
    if (!token) {
      setLoading(false);
      return;
    }
    refreshMe().finally(() => setLoading(false));
  }, [refreshMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setAuthToken(data.access_token);
      setUser(data.user);
      setCompany(data.company);
      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: extractErr(e, "Login failed") };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      setAuthToken(data.access_token);
      setUser(data.user);
      setCompany(data.company);
      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: extractErr(e, "Registration failed") };
    }
  };

  const logout = () => {
    setAuthToken(null);
    setUser(null);
    setCompany(null);
  };

  const updateCompany = (partial) => setCompany((c) => ({ ...c, ...partial }));

  return (
    <AuthCtx.Provider value={{ user, company, loading, login, register, logout, refreshMe, updateCompany }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
